import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { DiceRoll } from '@dice-roller/rpg-dice-roller';
import { MemoryDTO, MemoryType, MemoryValue } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { Character } from 'dorian-shared/types/character/Character';
import { BranchStepMemoryRoll, BranchStepMemoryRollType } from '../../../../../dorian-shared/types/branch/BranchStep';
import { MemoryBankCheckValue } from './MemorySlotCompareForm';

export const RandomRollType: Record<string, { name: string, title: string}> = {
  SET: {
    name: BranchStepMemoryRollType.Set,
    title: 'random between',
  },
  INCREASE: {
    name: BranchStepMemoryRollType.Increase,
    title: 'went up by random between',
  },
  DECREASE: {
    name: BranchStepMemoryRollType.Decrease,
    title: 'went down by random between',
  },
  DICE: {
    name: BranchStepMemoryRollType.Dice,
    title: 'dice notation',
  }
};

type StepTypeRandomProps = {
  stepIndex: number,
  memoryBankSlots: MemoryDTO[],
  handleChangeStepRoll: (
    values: MemoryBankCheckValue[],
    stepIndex: number,
  ) => void,
  roll: BranchStepMemoryRoll,
  addStepRoll: (
    stepIndex: number,
    variableId: number,
    variable: string,
    type: string,
    min?: MemoryValue,
    max?: MemoryValue,
    modifier?: number,
    diceNotation?: string,
  ) => void,
  characters: Character[],
  isDisabled?: boolean,
}

export function StepTypeRandom(props: StepTypeRandomProps) {
  const {
    stepIndex, memoryBankSlots, handleChangeStepRoll, roll, addStepRoll, characters,
    isDisabled,
  } = props;

  const [diceNotationError, setDiceNotationError] = useState<string | null>(null);

  const variableInputValue = roll && roll.variableId ? roll.variableId : 0;
  const rollType = roll && roll.type ? roll.type : '';
  const isRollAvailable = Object.values(BranchStepMemoryRollType).includes(rollType as BranchStepMemoryRollType);
  const typeInputValue = isRollAvailable ? rollType : '';
  const minInputValue = roll && 'min' in roll && roll.min ? roll.min : '';
  const maxInputValue = roll && 'max' in roll && roll.max ? roll.max : '';
  const modifierInputValue = roll && 'modifier' in roll && roll.modifier !== undefined ? roll.modifier.toString() : '';
  const diceNotationInputValue = roll && 'diceNotation' in roll && roll.diceNotation ? roll.diceNotation : '';

  const isRandomStepPossible = memoryBankSlots && memoryBankSlots.filter((slot) => slot.type === MemoryType.Number).length > 0;
  const memoryBankSlot = memoryBankSlots.find((slot) => slot.id === Number(variableInputValue));

  useEffect(() => {
    if (!isRandomStepPossible) {
      return;
    }

    // prevent re-adding roll object if already exists
    if (roll?.variableId !== undefined && roll?.type !== undefined) {
      return;
    }

    const variable = memoryBankSlots.filter((slot) => slot.type === MemoryType.Number)[0];
    const type = RandomRollType.SET.name;
    const min = '';
    const max = '';
    addStepRoll(stepIndex, variable.id, variable.name, type, min, max, 0, '');
  }, [roll, addStepRoll, isRandomStepPossible, memoryBankSlots, stepIndex]);

  const handleVariableChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const values = [
      {
        name: 'variableId',
        value: event.target.value,
      },
    ];

    const newMemory = memoryBankSlots.find((slot) => slot.id === Number(event.target.value));
    if (memoryBankSlot?.type !== newMemory?.type) {
      // Only reset type when changing to a different memory type
      values.push({ name: 'type', value: RandomRollType.SET.name });
      values.push({ name: 'min', value: '' });
      values.push({ name: 'max', value: '' });
      values.push({ name: 'modifier', value: '' });
      values.push({ name: 'diceNotation', value: '' });
    }
    handleChangeStepRoll(values, stepIndex);
  };

  const handleRollChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    const values = [{
      name: 'type',
      value: newValue,
    }];

    handleChangeStepRoll(values, stepIndex);
  };

  const handleModifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const values = [{
      name: 'modifier',
      value: event.target.value,
    }];
    handleChangeStepRoll(values, stepIndex);
  };

  const handleDiceNotationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const notation = event.target.value;
    
    // Clear any existing error when user starts typing
    if (diceNotationError) {
      setDiceNotationError(null);
    }
    
    const values = [{
      name: 'diceNotation',
      value: notation,
    }];
    handleChangeStepRoll(values, stepIndex);
  };

  const handleDiceNotationBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const notation = event.target.value;
    
    // Only validate on blur if there's content
    if (notation.trim() !== '') {
      try {
        new DiceRoll(notation);
        setDiceNotationError(null);
      } catch (error) {
        setDiceNotationError(`Invalid dice notation: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      setDiceNotationError(null);
    }
  };

  const availableRolls = Object.keys(RandomRollType);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      { isRandomStepPossible
        ? (
          <Col>
            <Row
              xs={1}
              sm={1}
              lg={4}
            >
              <Form.Group
                as={Col}
                lg={3}
                className="mb-0 px-1 d-block"
              >
                <Row>
                  <Col>
                    <Form.Control
                      disabled={isDisabled}
                      size="sm"
                      as="select"
                      name={`steps[${stepIndex}][roll][variableId]`}
                      value={variableInputValue}
                      onChange={handleVariableChange}
                    >
                      {memoryBankSlots.filter((slot) => slot.type === MemoryType.Number).map((slot) => (
                        <option key={slot.id} value={slot.id}>
                          {slot.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>
                </Row>
              </Form.Group>
              <Form.Group
                as={Col}
                lg={3}
                className="mb-0 px-1 d-block"
              >
                <Form.Control
                  disabled={isDisabled}
                  size="sm"
                  as="select"
                  name={`steps[${stepIndex}][roll][type]`}
                  value={typeInputValue}
                  onChange={handleRollChange}
                >
                  {availableRolls.map((rollTypeKey) => {
                    const inputRoll = RandomRollType[rollTypeKey];
                    return (
                      <option key={inputRoll.name} value={inputRoll.name}>
                        {RandomRollType[rollTypeKey].title}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
              <Col lg={6} className="mb-0 px-1 d-block">
                <Row>
                  {rollType === BranchStepMemoryRollType.Dice ? (
                    <Col md={12}>
                      <Form.Control
                        disabled={isDisabled}
                        size="sm"
                        type="text"
                        name={`steps[${stepIndex}][roll][diceNotation]`}
                        value={diceNotationInputValue}
                        placeholder="e.g., 2d6+3, 1d20, 3d8-1"
                        onChange={handleDiceNotationChange}
                        onBlur={handleDiceNotationBlur}
                        isInvalid={!!diceNotationError}
                      />
                      {diceNotationError && (
                        <Form.Control.Feedback type="invalid">
                          {diceNotationError}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  ) : (
                    <>
                      <Col md={2}>
                        <Form.Control
                          disabled={isDisabled}
                          size="sm"
                          type="text"
                          name={`steps[${stepIndex}][roll][min]`}
                          value={minInputValue}
                          placeholder="Min value"
                          onChange={(e) => handleChangeStepRoll([{ name: 'min', value: e.target.value }], stepIndex)}
                        />
                      </Col>
                      <Col md={1} className="d-flex align-items-center justify-content-center">
                        <span>and</span>
                      </Col>
                      <Col md={2}>
                        <Form.Control
                          disabled={isDisabled}
                          size="sm"
                          type="text"
                          name={`steps[${stepIndex}][roll][max]`}
                          value={maxInputValue}
                          placeholder="Max value"
                          onChange={(e) => handleChangeStepRoll([{ name: 'max', value: e.target.value }], stepIndex)}
                        />
                      </Col>
                      <Col md={1} className="d-flex align-items-center justify-content-center">
                        <span>+</span>
                      </Col>
                      <Col md={4}>
                        <Form.Control
                          disabled={isDisabled}
                          size="sm"
                          type="text"
                          name={`steps[${stepIndex}][roll][modifier]`}
                          value={modifierInputValue}
                          placeholder="optional modifier"
                          onChange={handleModifierChange}
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
        )
        : <Row>No number-type memory bank variables available.</Row>}
    </>
  );
}
